import { Component, forceUpdate, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { PrefillData } from "../../components";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";

type UxModal = HTMLElement & { triggerClose: () => void; triggerOpen: () => void };

@Component({
    tag: "mf-create-service-case-modal",
    shadow: false
})
export class CreateServiceCaseModal {
    private modal: UxModal;

    /**
     * Enable file upload in the form
     */
    @Prop() fileUpload: boolean = false;
    /**
     * data that should be prefilled in the form
     * @optional
     */
    @Prop() prefillData: PrefillData | null = null;
    /**
     * Identifier for the modal - see ux.ui docs
     */
    @Prop() modalName: string;
    /**
     * The locale to use for translations and the phone input
     */
    @Prop({ reflect: true }) locale: string = "en-US";
    /**
     * Environment string to decide which endpoints to use
     */
    @Prop() env: string = "prod";
    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    @Prop() legacyApi: boolean = false;
    /**
     * open modal automatically
     */
    @Prop() open: boolean = false;
    /** URL to be opened when the modal is closed */
    @Prop() reloadUrl: string = "";
    /**
     * origin application of the request, e.g. 'oseon' or 'MyTRUMPF'
     */
    @Prop() origin: string = "";

    @State() submitted: boolean = false;

    @Listen("mfServiceCaseSuccess")
    handleServiceCaseSuccess() {
        this.modal.triggerClose();
    }

    @Listen("mfServiceCaseCancel")
    handleServiceCaseCancel() {
        this.modal.triggerClose();
    }

    private handleModalClose = () => {
        if (this.reloadUrl && this.reloadUrl.length > 0) {
            window.location.href = this.reloadUrl;
            return;
        }
        window.location.reload();
    };

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.onLocaleChange();
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentDidLoad() {
        if (this.open) {
            this.modal.triggerOpen();
        }
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    @Listen("mfServiceCaseShowSuccess")
    handleServiceCaseShowSuccess() {
        this.submitted = true;
    }

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    render() {
        const t = (key: TranslationKey, options: unknown = undefined) =>
            i18nProvider.t(`mf-service-calls:${String(key)}`, options as Record<string, unknown>);
        return (
            <Host>
                <ux-modal
                    onUxModalClose={() => this.handleModalClose()}
                    name={this.modalName}
                    ref={(el: UxModal) => (this.modal = el)}
                >
                    <ux-modal-content>
                        <mf-create-service-case
                            fileUpload={this.fileUpload}
                            prefillData={this.prefillData}
                            multipleSteps={true}
                            locale={this.locale}
                            env={this.env}
                            legacyApi={this.legacyApi}
                            origin={this.origin}
                        >
                            <slot name="connector" />
                        </mf-create-service-case>
                    </ux-modal-content>
                    {this.submitted && (
                        <ux-modal-footer>
                            <ux-button
                                variant="primary"
                                onClick={() => {
                                    this.modal.triggerClose();
                                }}
                            >
                                {t("button.ok")}
                            </ux-button>
                        </ux-modal-footer>
                    )}
                </ux-modal>
            </Host>
        );
    }
}
